import React, { useCallback, useState } from "react";
import { cn } from "../../../../lib/utils";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import SelectDropdownV2 from "../../../../components/ui/selectdropdown/v2";

export type FormFieldType = "text" | "number" | "select" | "date";

export interface FormFieldProps {
  type: FormFieldType;
  label: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  options?: OptionNameValuePair<string>[];
  prefixIcon?: React.ReactNode;
  hideLabel?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  className?: string;
}

const inputBase =
  "w-full text-sm outline-none bg-transparent placeholder:text-[#737373] disabled:cursor-not-allowed";
const boxBase =
  "flex items-center border rounded-md bg-white h-9 px-3 gap-2 disabled:bg-gray-100";

/** Compact, type-driven form cell built on the shared inputs/dropdown. */
const FormField: React.FC<FormFieldProps> = ({
  type,
  label,
  value,
  required,
  placeholder,
  disabled,
  error,
  options = [],
  prefixIcon,
  hideLabel,
  onChange,
  onBlur,
  className
}) => {
  const boxBorder = error ? "border-red-400" : "border-[#E5E5E5]";

  const renderControl = () => {
    if (type === "select") {
      return (
        <SelectDropdownV2
          name={label}
          placeholder={placeholder ?? `Select ${label}`}
          selectedOption={options.find((o) => o.value === value)}
          options={options}
          disabled={disabled}
          enableSearch={options.length > 6}
          containerClassName={cn(
            "border rounded-md bg-white h-9",
            boxBorder,
            disabled && "bg-gray-100",
            className
          )}
          labelClassName="py-2 px-3"
          onSelectionChange={(opt) => {
            onChange(opt.value);
            onBlur?.();
          }}
        />
      );
    }

    return (
      <div className={cn(boxBase, boxBorder, disabled && "bg-gray-100", className)}>
        {prefixIcon && <span className="shrink-0 text-gray-500">{prefixIcon}</span>}
        <input
          type={type === "number" ? "number" : type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          className={inputBase}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className={cn("text-xs font-medium text-gray-700", hideLabel && "sr-only")}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {renderControl()}
      {error && <span className="text-xs text-red-500 whitespace-pre-line">{error}</span>}
    </div>
  );
};

export default FormField;

/** Tracks which field keys have been blurred, so errors show only after touch. */
export const useTouched = () => {
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const markTouched = useCallback((key: string) => {
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const isTouched = useCallback((key: string) => touched.has(key), [touched]);

  return { markTouched, isTouched };
};
